import React, { useState, useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

const FiXComponent = FiX as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

interface SnapshotStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    segmentId: string;
    snapshotId: string;
}

const SnapshotStatusModal: React.FC<SnapshotStatusModalProps> = ({
    isOpen,
    onClose,
    segmentId,
    snapshotId,
}) => {
    const [status, setStatus] = useState<string>('PENDING');
    const [counts, setCounts] = useState<{ total: number } | null>(null);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [snapshotMembers, setSnapshotMembers] = useState<any[]>([]);
    const [membersTotalCount, setMembersTotalCount] = useState(0);
    const [membersPage, setMembersPage] = useState(0);
    // const [isReady, setIsReady] = useState(false);
    const membersPerPage = 6;
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isPollingStoppedRef = useRef<boolean>(false);
    const hasFetchedMembersRef = useRef<boolean>(false);

    // Fetch snapshot members
    const fetchSnapshotMembers = async (page: number = 0) => {
        try {
            setIsLoading(true);
            const { token, tenantId, userId } = getAuthContext();
            if (!token) return;

            const response = await fetch(
                `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots/${snapshotId}/members?size=${membersPerPage}&page=${page}`,
                {
                    method: 'GET',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            setSnapshotMembers(data.content || []);
            setMembersTotalCount(data.totalCount || 0);
            setMembersPage(page);
            // setIsReady(true);
            hasFetchedMembersRef.current = true;

            // Stop polling once members are loaded and displayed
            stopPolling();

        } catch (error) {
            // console.error('❌ Failed to fetch snapshot members:', error);
            toast.error('Failed to load snapshot members');
        } finally {
            setIsLoading(false);
        }
    };

    // Stop polling
    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            isPollingStoppedRef.current = true;
        }
    };

    // Reset all states when modal opens with new snapshot
    useEffect(() => {
        if (!isOpen) {
            // Clean up when modal closes
            stopPolling();
            return;
        }

        // Reset states for new snapshot
        setStatus('PENDING');
        setCounts(null);
        setCreatedAt(null);
        setSnapshotMembers([]);
        setMembersTotalCount(0);
        setMembersPage(0);
        // setIsReady(false);
        isPollingStoppedRef.current = false;
        hasFetchedMembersRef.current = false;

        let pollInterval: NodeJS.Timeout | null = null;

        const checkSnapshotStatus = async () => {
            // Don't check if polling is already stopped or members already fetched
            if (isPollingStoppedRef.current || hasFetchedMembersRef.current) return;

            try {
                const { token, tenantId, userId } = getAuthContext();
                if (!token) return;

                const response = await fetch(
                    `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots/${snapshotId}`,
                    {
                        method: 'GET',
                        headers: {
                            'X-Tenant-Id': tenantId,
                            'X-User-Id': userId || '',
                            'Authorization': `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();

                setStatus(data.status || 'PENDING');
                setCounts(data.counts || null);
                setCreatedAt(data.createdAt || null);

                // When status becomes READY, fetch members
                if (data.status === 'READY' && !hasFetchedMembersRef.current) {
                    // Stop polling first
                    stopPolling();
                    // Then fetch members
                    await fetchSnapshotMembers(0);
                }
            } catch (error) {
                // console.error('❌ Failed to check snapshot status:', error);
                toast.error('Failed to check snapshot status');
            }
        };

        // Initial check
        checkSnapshotStatus();

        // Start polling every 2 seconds
        pollInterval = setInterval(checkSnapshotStatus, 2000);
        pollIntervalRef.current = pollInterval;

        // Cleanup on unmount or modal close
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
                pollIntervalRef.current = null;
            }
            isPollingStoppedRef.current = true;
        };
    }, [isOpen, segmentId, snapshotId]);

    const handleMembersPageChange = async (newPage: number) => {
        if (newPage < 0 || newPage >= Math.ceil(membersTotalCount / membersPerPage)) return;

        // Fetch members for new page - polling is already stopped
        try {
            setIsLoading(true);
            const { token, tenantId, userId } = getAuthContext();
            if (!token) return;

            const response = await fetch(
                `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots/${snapshotId}/members?size=${membersPerPage}&page=${newPage}`,
                {
                    method: 'GET',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            setSnapshotMembers(data.content || []);
            setMembersPage(newPage);
        } catch (error) {
            // console.error('❌ Failed to fetch snapshot members:', error);
            toast.error('Failed to load snapshot members');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const totalMembersPages = Math.ceil(membersTotalCount / membersPerPage);

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg w-[600px] max-w-[90%] max-h-[90vh] flex flex-col">
                {/* Fixed Header */}
                <div className="flex-shrink-0 p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-[#3F5265]">
                            Snapshot
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <FiXComponent size={20} />
                        </button>
                    </div>

                    {/* Status Display */}
                    <div className="mt-2 flex gap-4 flex-wrap">
                        <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-[#657584]">Status:</span>
                            <span className={`text-sm font-semibold ${status === 'READY' ? 'text-green-600' : 'text-orange-500'
                                }`}>
                                {status === 'READY' ? 'Ready' : (
                                    <span className="flex items-center gap-1">
                                        Pending
                                        <span className="inline-flex gap-1">
                                            <span className="animate-bounce">.</span>
                                            <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                                            <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>.</span>
                                        </span>
                                    </span>
                                )}
                            </span>
                        </div>

                        {counts && status === 'READY' && (
                            <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-[#657584]">Total Members:</span>
                                <span className="text-sm text-[#3F5265]">{counts.total}</span>
                            </div>
                        )}

                        {createdAt && status === 'READY' && (
                            <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-[#657584]">Created At:</span>
                                <span className="text-sm text-[#3F5265]">
                                    {new Date(createdAt).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: true,
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {/* Loading State */}
                    {isLoading && snapshotMembers.length === 0 && status === 'READY' && (
                        <div className="text-center py-8 text-gray-500">Loading members...</div>
                    )}

                    {/* Snapshot Members Table - Auto displayed when ready */}
                    {status === 'READY' && !isLoading && (
                        <div className="mt-2">
                            {snapshotMembers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">No members found</div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Email
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {snapshotMembers.map((member: any, index: number) => (
                                                    <tr key={member.id || index}>
                                                        <td className="px-4 py-2 text-sm text-[#3F5265]">
                                                            {member.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-[#657584]">
                                                            {member.email || 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination for members */}
                                    {totalMembersPages > 1 && (
                                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 mt-4">
                                            <span className="text-sm text-gray-500">
                                                Showing {snapshotMembers.length} members out of {membersTotalCount} total
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    disabled={membersPage === 0}
                                                    onClick={() => handleMembersPageChange(membersPage - 1)}
                                                    className="px-3 py-1 border rounded text-sm bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Prev
                                                </button>
                                                <span className="text-sm text-gray-600">
                                                    {membersPage + 1} of {totalMembersPages}
                                                </span>
                                                <button
                                                    disabled={membersPage === totalMembersPages - 1}
                                                    onClick={() => handleMembersPageChange(membersPage + 1)}
                                                    className="px-3 py-1 border rounded text-sm bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SnapshotStatusModal;